/**
 * Authentication Context - Simplified for Google SSO Only
 * 
 * Provides authentication state and methods for Google OAuth sign-in.
 * No email/password, sign-up, or password reset functionality.
 */
import React, { createContext, useContext, useState, useEffect } from 'react';
import { 
  signInWithRedirect, 
  signOut as amplifySignOut,
  getCurrentUser,
  fetchAuthSession,
  fetchUserAttributes
} from 'aws-amplify/auth';
import axios from 'axios';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [syncInProgress, setSyncInProgress] = useState(false);

  // API base URL
  const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000';

  /**
   * Check authentication status on mount and after OAuth callback
   */
  useEffect(() => {
    checkAuthStatus();
  }, []);

  /**
   * Check if user is authenticated
   */
  const checkAuthStatus = async () => {
    try {
      console.log('🔍 Checking authentication status...');
      
      // Check for OAuth callback parameters
      const urlParams = new URLSearchParams(window.location.search);
      const hasOAuthCode = urlParams.has('code');
      
      if (hasOAuthCode) {
        console.log('🔐 OAuth callback detected, processing...');
        // Clear URL parameters after processing
        window.history.replaceState({}, document.title, window.location.pathname);
      }

      // Try to get current user
      const cognitoUser = await getCurrentUser();
      console.log('✅ User authenticated:', cognitoUser.username);

      // Get user attributes
      const attributes = await fetchUserAttributes();
      console.log('📋 User attributes:', attributes);

      // Sync user with backend
      await syncUserWithBackend(cognitoUser, attributes);

    } catch (error) {
      console.log('ℹ️ No authenticated user:', error.message);
      setUser(null);
    } finally {
      setLoading(false);
    }
  };

  /**
   * Sign in with Google OAuth
   */
  const signInWithGoogle = async () => {
    try {
      console.log('🔐 Initiating Google sign-in...');
      await signInWithRedirect({ provider: 'Google' });
    } catch (error) {
      console.error('❌ Error signing in with Google:', error);
      throw error;
    }
  };

  /**
   * Sign out user
   */
  const signOut = async () => {
    try {
      console.log('🚪 Signing out...');
      await amplifySignOut();
      setUser(null);
      console.log('✅ Signed out successfully');
    } catch (error) {
      console.error('❌ Error signing out:', error);
      throw error;
    }
  };

  /**
   * Sync user with backend database
   */
  const syncUserWithBackend = async (cognitoUser, attributes) => {
    if (syncInProgress) {
      console.log('⏳ Sync already in progress, skipping...');
      return;
    }

    setSyncInProgress(true);

    try {
      console.log('🔄 Syncing user with backend...');

      // Get ID token for authentication
      const session = await fetchAuthSession();
      const idToken = session.tokens?.idToken?.toString();

      if (!idToken) {
        throw new Error('No ID token available');
      }

      // Extract user information
      const userName = attributes.name || 
                       `${attributes.given_name || ''} ${attributes.family_name || ''}`.trim() || 
                       attributes.email?.split('@')[0] || 
                       'User';

      const provider = cognitoUser.username?.startsWith('Google_') ? 'google' : 'cognito';

      // Prepare payload
      const payload = {
        email: attributes.email,
        name: userName,
        cognito_id: cognitoUser.userId || cognitoUser.username,
        provider: provider,
        last_login: new Date().toISOString(),
        upsert: true
      };

      console.log('📤 Sending user data to backend:', payload);

      // Send to backend
      const response = await axios.post(
        `${API_BASE_URL}/api/v1/auth/users`,
        payload,
        {
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${idToken}`
          }
        }
      );

      if (response.status === 200 || response.status === 201) {
        const backendUser = response.data;
        console.log('✅ User synced successfully:', backendUser);

        // Set user state
        setUser({
          user_id: backendUser.user_id,
          email: backendUser.email,
          name: backendUser.name,
          role: backendUser.role || 'user',
          status: backendUser.status || 'active',
          provider: provider,
          cognito_id: cognitoUser.userId || cognitoUser.username
        });
      }
    } catch (error) {
      console.error('❌ Failed to sync user with backend:', error);
      
      // Set user state with Cognito data even if backend sync fails
      setUser({
        email: attributes.email,
        name: attributes.name || 'User',
        provider: cognitoUser.username?.startsWith('Google_') ? 'google' : 'cognito',
        cognito_id: cognitoUser.userId || cognitoUser.username,
        synced: false
      });
    } finally {
      setSyncInProgress(false);
    }
  };

  /**
   * Get ID token for API calls
   */
  const getIdToken = async () => {
    try {
      const session = await fetchAuthSession();
      return session.tokens?.idToken?.toString();
    } catch (error) {
      console.error('❌ Error getting ID token:', error);
      return null;
    }
  };

  const value = {
    user,
    loading,
    signInWithGoogle,
    signOut,
    getIdToken,
    isAuthenticated: !!user
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
